import { useState, useCallback } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { CheckCircle2 } from "lucide-react"

type ResolutionOutcome = 'upheld' | 'denied' | 'partial'

interface ResolveDisputeModalProps {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
  readonly disputeId?: string
  readonly customerName?: string
  readonly onResolve: (outcome: ResolutionOutcome, amount: string, notes: string) => Promise<void>
  readonly idPrefix?: string
}

export function ResolveDisputeModal({
  open,
  onOpenChange,
  disputeId,
  customerName,
  onResolve,
  idPrefix = "",
}: Readonly<ResolveDisputeModalProps>) {
  const [resolutionOutcome, setResolutionOutcome] = useState<ResolutionOutcome>('upheld')
  const [resolutionAmount, setResolutionAmount] = useState('')
  const [resolutionNotes, setResolutionNotes] = useState('')

  const resetForm = useCallback(() => {
    setResolutionOutcome('upheld')
    setResolutionAmount('')
    setResolutionNotes('')
  }, [])

  const handleOpenChange = useCallback((o: boolean) => {
    onOpenChange(o)
    if (!o) {
      resetForm()
    }
  }, [onOpenChange, resetForm])

  const handleCancel = useCallback(() => {
    onOpenChange(false)
    resetForm()
  }, [onOpenChange, resetForm])

  const handleResolve = useCallback(async () => {
    if (!resolutionNotes.trim()) return
    if (resolutionOutcome === 'partial' && !resolutionAmount.trim()) return
    await onResolve(resolutionOutcome, resolutionAmount, resolutionNotes)
    resetForm()
  }, [resolutionOutcome, resolutionAmount, resolutionNotes, onResolve, resetForm])

  const isDisabled = !resolutionNotes.trim() || (resolutionOutcome === 'partial' && !resolutionAmount.trim())

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            Complete Resolution
          </DialogTitle>
          <DialogDescription>
            Complete resolution for dispute <span className="font-semibold">{disputeId}</span> — {customerName}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Resolution Outcome */}
          <div className="space-y-2">
            <Label>Resolution Outcome *</Label>
            <RadioGroup value={resolutionOutcome} onValueChange={(value) => setResolutionOutcome(value as ResolutionOutcome)}>
              <div className="flex items-start space-x-2 rounded-lg border p-3 hover:bg-accent">
                <RadioGroupItem value="upheld" id={`${idPrefix}upheld`} className="mt-1" />
                <div className="flex-1">
                  <Label htmlFor={`${idPrefix}upheld`} className="font-medium cursor-pointer">
                    Upheld (Customer Won)
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    The customer's dispute is valid. Transaction will be reversed.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-2 rounded-lg border p-3 hover:bg-accent">
                <RadioGroupItem value="denied" id={`${idPrefix}denied`} className="mt-1" />
                <div className="flex-1">
                  <Label htmlFor={`${idPrefix}denied`} className="font-medium cursor-pointer">
                    Denied (Merchant Won)
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Dispute is not valid. Transaction stands as is.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-2 rounded-lg border p-3 hover:bg-accent">
                <RadioGroupItem value="partial" id={`${idPrefix}partial`} className="mt-1" />
                <div className="flex-1">
                  <Label htmlFor={`${idPrefix}partial`} className="font-medium cursor-pointer">
                    Partial Resolution
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Partial credit issued to customer.
                  </p>
                </div>
              </div>
            </RadioGroup>
          </div>

          {/* Resolution Amount (only for partial) */}
          {resolutionOutcome === 'partial' && (
            <div className="space-y-2">
              <Label htmlFor={`${idPrefix}resolutionAmount`}>Resolution Amount *</Label>
              <Input
                id={`${idPrefix}resolutionAmount`}
                type="number"
                step="0.01"
                placeholder="Enter amount (e.g., 2500.00)"
                value={resolutionAmount}
                onChange={(e) => setResolutionAmount(e.target.value)}
              />
            </div>
          )}

          {/* Resolution Notes */}
          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}resolutionNotes`}>Resolution Notes *</Label>
            <Textarea
              id={`${idPrefix}resolutionNotes`}
              placeholder="Explain the resolution reasoning and any actions taken..."
              value={resolutionNotes}
              onChange={(e) => setResolutionNotes(e.target.value)}
              rows={4}
              className="resize-none"
            />
            <p className="text-xs text-muted-foreground">
              Provide detailed notes about why this resolution was reached and any relevant context.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            onClick={handleResolve}
            disabled={isDisabled}
            className="bg-green-600 hover:bg-green-700"
          >
            <CheckCircle2 className="h-4 w-4 mr-2" />
            Complete Resolution
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
